import { CSSProperties, forwardRef, useImperativeHandle, useState } from "react";
import { Popup } from "semantic-ui-react";
import { Link2, MessageCircleMore } from "lucide-react";
import { useIntl } from "react-intl";

// Components
import ModMeBillingCustomer from "react-lib/apps/MEH/ModMeBillingCustomer";
import ModLinkedCustomer, { LinkedCustomerFormData } from "react-lib/apps/MEH/ModLinkedCustomer";
import { actionButtonStyle } from "react-lib/apps/MEH/CustomerInfoHeader";

// Types
import {
  PickedState,
  SelectedCustomerData,
  CreateCustomerParams,
  RunSequence,
} from "react-lib/apps/MEH/types/MeBillingType";

export type MeBillingCustomerActionsRef = {
  onEdit: () => void;
  onChat: () => void;
  onLinkUserId: () => void;
};

type MeBillingCustomerActionsProps = {
  setProp: any;
  runSequence: RunSequence;
  // data
  selectedCustomer: SelectedCustomerData | undefined;
  masterData: any;
  // callback
  onChat: () => void;
  onSaved?: () => void;
  languageUX: "en" | "th";
  // ซ่อนแถวไอคอน ใช้ปุ่มภายนอกเรียกผ่าน ref แทน
  hideButtons?: boolean;
} & Pick<PickedState, "buttonLoadCheck" | "masterOptions">;

const IMAGES = {
  edit: "/static/images/meBilling/icon_edit.png",
};

const getCustomerFormData = (
  selectedCustomer: SelectedCustomerData | undefined,
): CreateCustomerParams => {
  const patientData = selectedCustomer?.patientDetail;
  const emailValue = patientData?.email || patientData?.present_address?.email || "";

  const { created, edited, ...relative_address } = patientData?.relative_address ?? {};

  return {
    id: patientData?.id,
    hn: patientData?.hn,
    // -- iden
    citizen_type: patientData?.citizen_type || ("" as any),
    citizen_no: patientData?.citizen_no || "", // --- citizen_type === "T"
    passport_no: patientData?.passport_no || "", // --- citizen_type === "F"
    // -- thai name
    pre_name_th: patientData?.pre_name_th || 0,
    first_name_th: patientData?.first_name_th || "",
    last_name_th: patientData?.last_name_th || "",
    // -- eng name
    pre_name_en: patientData?.pre_name_en || 0,
    first_name_en: patientData?.first_name_en || "",
    middle_name_en: patientData?.middle_name_en || "",
    last_name_en: patientData?.last_name_en || "",
    //
    birthdate: patientData?.birthdate || "",
    gender: patientData?.gender || "",
    // -- address
    present_address: patientData?.present_address,
    // -- extended
    email: emailValue || "",
    line_id: patientData?.line_id || "",
    profile_image: patientData?.profile_image || null,
    // -- relative_address
    relative_address,

    // display only
    display_hn: patientData?.division_hn || "",
  };
};

// ปุ่มแก้ไข / แชท / เชื่อมไอดี ของผู้รับบริการ (MePOS)
const MeBillingCustomerActions = forwardRef<
  MeBillingCustomerActionsRef,
  MeBillingCustomerActionsProps
>((props, ref) => {
  const intl = useIntl();

  const [editCustomerData, setEditCustomerData] = useState<CreateCustomerParams | null>(null);
  const [openLinkedCustomerMod, setOpenLinkedCustomerMod] = useState<boolean>(false);

  const patientId = props.selectedCustomer?.patientDetail?.id;

  const handleEdit = () => {
    setEditCustomerData(getCustomerFormData(props.selectedCustomer));
  };

  const handleCloseEdit = () => {
    setEditCustomerData(null);
  };

  const handleSaveEdit = (data: CreateCustomerParams) => {
    if (!editCustomerData?.id) return;

    props.runSequence({
      sequence: "MeBilling",
      action: "UPDATE_CUSTOMER",
      customerId: editCustomerData.id,
      customerData: data,
      onSuccess: () => {
        handleCloseEdit();
        props.onSaved?.();
      },
    });
  };

  const handleChat = () => {
    if (!patientId) return;

    props.setProp("openPatientChatId", patientId);
    props.onChat();
  };

  const handleSaveLinked = (data: LinkedCustomerFormData) => {
    if (!data.customer_id) return;

    props.runSequence({
      sequence: "MeBilling",
      action: "LINK_CUSTOMER",
      customerId: Number(data.customer_id),
      linkedId: data.linked_id,
      onSuccess: () => setOpenLinkedCustomerMod(false),
    });
  };

  const handleOpenLinked = () => {
    setOpenLinkedCustomerMod(true);
  };

  useImperativeHandle(ref, () => ({
    onEdit: handleEdit,
    onChat: handleChat,
    onLinkUserId: handleOpenLinked,
  }));

  if (!props.selectedCustomer) return null;

  return (
    <>
      {!props.hideButtons && (
        <div style={styles.actions}>
          <Popup
            content={intl.formatMessage({ id: "แก้ไขข้อมูล" })}
            position="top center"
            size="mini"
            trigger={
              <div style={actionButtonStyle} onClick={handleEdit}>
                <img style={{ width: "20px", height: "20px" }} alt="edit" src={IMAGES.edit} />
              </div>
            }
          />
          <Popup
            content={intl.formatMessage({ id: "แชท" })}
            position="top center"
            size="mini"
            trigger={
              <div style={actionButtonStyle} onClick={handleChat}>
                <MessageCircleMore size={18} color="#3C3C3C" />
              </div>
            }
          />
          <Popup
            content={intl.formatMessage({ id: "CUSTOMER_INFO_เชื่อมไอดีผู้ใช้งาน" })}
            position="top center"
            size="mini"
            trigger={
              <div style={actionButtonStyle} onClick={handleOpenLinked}>
                <Link2 size={18} color="#3C3C3C" />
              </div>
            }
          />
        </div>
      )}

      <ModMeBillingCustomer
        open={!!editCustomerData}
        onClose={handleCloseEdit}
        onSave={handleSaveEdit}
        setProp={props.setProp}
        initialData={editCustomerData}
        masterData={props.masterData}
        masterOptions={props.masterOptions}
        buttonLoadCheck={props.buttonLoadCheck}
        languageUX={props.languageUX}
      />

      <ModLinkedCustomer
        open={openLinkedCustomerMod}
        onClose={() => setOpenLinkedCustomerMod(false)}
        onSave={handleSaveLinked}
        customerId={patientId}
        customerName={props.selectedCustomer?.patientDetail?.full_name}
        buttonLoadCheck={props.buttonLoadCheck}
        languageUX={props.languageUX}
      />
    </>
  );
});

const styles: Record<string, CSSProperties> = {
  actions: {
    display: "flex",
    justifyContent: "center",
    gap: "0.75rem",
  },
};

MeBillingCustomerActions.displayName = "MeBillingCustomerActions";

export default MeBillingCustomerActions;
